// UnitItemsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import HeadingCp from "../components/HeadingCp";
import { useNavigate } from "react-router-dom";

const toNum = (v, d = 0) => {
  const n = typeof v === "string" ? v.replace(/[, ]+/g, "") : v;
  const f = Number(n);
  return Number.isFinite(f) ? f : d;
};

export default function UnitItemsAdmin() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("ALL");
  const navigate = useNavigate();

  // Create/Edit modal state
  const emptyForm = {
    cat: "",
    device: "",
    description: "",
    sku: "",
    manufacturer: "",
    unit_price: "0.00",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null); // null=create, number=edit
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/unit_pricing_crud.php?item_catalog=1`, {
        withCredentials: true,
      });
      setRows(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load items", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [apiUrl]);

  const categories = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.cat && s.add(r.cat));
    return ["ALL", ...Array.from(s).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(r => {
      if (catFilter !== "ALL" && r.cat !== catFilter) return false;
      if (!needle) return true;
      const hay = `${r.device} ${r.description||""} ${r.sku||""} ${r.manufacturer||""} ${r.cat}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, search, catFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      cat: row.cat || "",
      device: row.device || "",
      description: row.description || "",
      sku: row.sku || "",
      manufacturer: row.manufacturer || "",
      unit_price: String(row.unit_price ?? "0.00"),
      notes: row.notes || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!saving) {
      setShowModal(false);
      setForm({ ...emptyForm });
      setEditingId(null);
    }
  };

  const saveItem = async () => {
    // basic validation
    if (!form.cat || !form.device) {
      toast.error("Category and Device are required.", { position: "top-center" });
      return;
    }
    const payload = {
      status: editingId ? "update_item" : "create_item",
      ...(editingId ? { id: editingId } : {}),
      item: {
        ...form,
        unit_price: toNum(form.unit_price, 0).toFixed(2),
      },
    };
    setSaving(true);
    try {
      const { data } = await axios.post(`${apiUrl}/unit_pricing_crud.php`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (data?.success) {
        toast.success(editingId ? "Item updated." : "Item created.", { position: "top-center" });
        setShowModal(false);
        setForm({ ...emptyForm });
        setEditingId(null);
        await load();
      } else {
        toast.error(data?.error || "Save failed.", { position: "top-center" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Save failed.", { position: "top-center" });
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const { data } = await axios.post(
        `${apiUrl}/unit_pricing_crud.php`,
        { status: "delete_item", id },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      if (data?.success) {
        toast.success("Item deleted.", { position: "top-center" });
        setRows(prev => prev.filter(r => r.id !== id));
      } else {
        toast.error(data?.error || "Delete failed.", { position: "top-center" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Delete failed.", { position: "top-center" });
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] p-4">
        <h1><HeadingCp label="Unit Items Admin" /></h1>
      <div className="flex items-center justify-between mb-3">
        {/* <h1 className="text-2xl font-bold">Unit Items</h1> */}
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
            placeholder="Search device / sku / manufacturer…"
            className="border rounded px-3 py-2 w-[280px]"
          />
          <select
            value={catFilter}
            onChange={(e)=>setCatFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={openCreate}
            className="bg-emerald-600 text-white px-3 py-2 rounded shadow hover:bg-emerald-700"
          >
            Add Item
          </button>
          <button
            onClick={() => navigate("/unit_pricing_estimate_list")}
            className="bg-emerald-600 text-white px-3 py-2 rounded shadow hover:bg-emerald-700"
          >
            Estimate Unit List
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className="px-3 py-2">Cat</th>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2">Mfr</th>
              <th className="px-3 py-2 text-right">Unit Price</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-3 py-2">{r.cat}</td>
                <td className="px-3 py-2 font-medium">{r.device}</td>
                <td className="px-3 py-2 text-gray-600 max-w-[320px] whitespace-pre-wrap">{r.description}</td>
                <td className="px-3 py-2">{r.sku}</td>
                <td className="px-3 py-2">{r.manufacturer}</td>
                <td className="px-3 py-2 text-right">${toNum(r.unit_price,0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={()=>openEdit(r)}
                      className="px-2 py-1 rounded border hover:bg-gray-50"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={()=>deleteItem(r.id)}
                      className="px-2 py-1 rounded border hover:bg-gray-50 text-red-600"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  {loading ? "Loading…" : "No items found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="absolute inset-x-0 top-10 mx-auto w-full max-w-2xl rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">{editingId ? "Edit Item" : "Add Item"}</div>
              <button onClick={closeModal} className="px-2 py-1 rounded hover:bg-gray-100">✕</button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="border rounded px-3 py-2"
                  placeholder="Category (e.g., supply_only)"
                  value={form.cat}
                  onChange={(e)=>setForm(f=>({...f, cat:e.target.value}))}
                />
                <input
                  className="border rounded px-3 py-2 md:col-span-2"
                  placeholder="Device"
                  value={form.device}
                  onChange={(e)=>setForm(f=>({...f, device:e.target.value}))}
                />
                <input
                  className="border rounded px-3 py-2"
                  placeholder="SKU"
                  value={form.sku}
                  onChange={(e)=>setForm(f=>({...f, sku:e.target.value}))}
                />
                <input
                  className="border rounded px-3 py-2"
                  placeholder="Manufacturer"
                  value={form.manufacturer}
                  onChange={(e)=>setForm(f=>({...f, manufacturer:e.target.value}))}
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="border rounded px-3 py-2"
                  placeholder="Unit Price"
                  value={form.unit_price}
                  onChange={(e)=>setForm(f=>({...f, unit_price:e.target.value}))}
                />
                <textarea
                  rows={4}
                  className="border rounded px-3 py-2 md:col-span-3"
                  placeholder="Description"
                  value={form.description}
                  onChange={(e)=>setForm(f=>({...f, description:e.target.value}))}
                />
                <textarea
                  rows={3}
                  className="border rounded px-3 py-2 md:col-span-3"
                  placeholder="Notes (optional)"
                  value={form.notes}
                  onChange={(e)=>setForm(f=>({...f, notes:e.target.value}))}
                />
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={closeModal} className="px-3 py-2 rounded border hover:bg-gray-50" disabled={saving}>
                  Cancel
                </button>
                <button
                  onClick={saveItem}
                  className="px-3 py-2 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving…" : (editingId ? "Update" : "Create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
